import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, ArrowUp, Wand2, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "Make it India-only",
  "Smaller budget",
  "Go bigger",
  "Shorter timeline",
  "Add a music video cutdown",
  "More editorial, less commercial",
];

const FIELD_LABELS = {
  title: "Title", tagline: "Tagline",
  executive_summary: "Executive summary",
  creative_direction: "Creative direction",
  moodboard: "Moodboard",
  production_type: "Format & scope",
  deliverables: "Deliverables",
  budget: "Budget",
  timeline: "Timeline",
  casting: "Casting",
  locations: "Locations",
  equipment: "Equipment",
  crew: "Crew",
  next_steps: "Next steps",
};
const label = (k) => FIELD_LABELS[k] || k;

/**
 * Multi-turn revision, keep talking to Your Producer inside the same modal.
 * Each successful rework becomes a turn in the running conversation.
 */
export default function ReviseModal({ blueprintId, initialBlueprint, onClose, onRevised }) {
  const [instruction, setInstruction] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [turns, setTurns] = useState([]);
  const [currentTitle, setCurrentTitle] = useState(initialBlueprint?.title || "");

  // Auto-scroll to bottom whenever new turns arrive
  useEffect(() => {
    const el = document.getElementById("revise-scroll");
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns, sending]);

  const submit = async () => {
    const text = instruction.trim();
    if (!text || sending) return;
    setSending(true);
    setError(null);
    // Optimistically show the user's message
    setTurns((prev) => [...prev, { role: "user", text }]);
    setInstruction("");
    try {
      const { data } = await axios.post(
        `${API}/blueprint/${blueprintId}/revise`,
        { instruction: text }
      );
      setTurns((prev) => [
        ...prev,
        {
          role: "producer",
          changed_fields: data.last_revision?.changed_fields || [],
          title: data.title,
          tagline: data.tagline,
        },
      ]);
      setCurrentTitle(data.title);
      onRevised?.(data);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.detail || "Your Producer couldn't rework that. Try again in a moment.");
      // remove the optimistic user turn on failure
      setTurns((prev) => prev.slice(0, -1));
      setInstruction(text);
    } finally {
      setSending(false);
    }
  };

  const hasHistory = turns.length > 0;

  return (
    <div
      className="fixed inset-0 z-[85] bg-white flex flex-col"
      data-testid="revise-modal"
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 md:px-10 py-5 border-b border-bnm shrink-0">
        <div>
          <div className="eyebrow eyebrow-denim inline-flex items-center gap-2">
            <Wand2 className="w-3.5 h-3.5" />
            Talking to Your Producer
          </div>
          {currentTitle && (
            <div className="text-[13px] text-muted-ink mt-1.5 truncate max-w-[440px]">
              About <span className="italic">&ldquo;{currentTitle}&rdquo;</span>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="close"
          className="w-10 h-10 rounded-full border border-bnm flex items-center justify-center hover:border-black"
          data-testid="revise-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Conversation */}
      <div id="revise-scroll" className="flex-1 overflow-y-auto px-6 md:px-10 py-10">
        <div className="max-w-[720px] mx-auto">
          {!hasHistory && (
            <div className="font-display text-[28px] md:text-[40px] font-extrabold tracking-[-0.035em] leading-[1.05] text-ink mb-8">
              What would you like <span className="font-serif-italic font-light">changed</span>?
            </div>
          )}

          {turns.map((t, i) =>
            t.role === "user" ? (
              <div key={`turn-${i}-${t.role}`} className="flex justify-end mb-5" data-testid={`turn-user-${i}`}>
                <div className="bg-ink text-white px-5 py-3 rounded-2xl rounded-tr-md max-w-[85%] text-[15px] leading-[1.55]">
                  {t.text}
                </div>
              </div>
            ) : (
              <div key={`turn-${i}-${t.role}`} className="mb-8" data-testid={`turn-producer-${i}`}>
                <div className="eyebrow eyebrow-denim mb-2 inline-flex items-center gap-2">
                  <Check className="w-3.5 h-3.5" />
                  Reworked
                </div>
                <div className="font-display font-semibold text-[18px] md:text-[20px] tracking-[-0.015em] leading-[1.25] text-ink">
                  {t.title}
                </div>
                {t.tagline && (
                  <div className="font-serif-italic text-[15.5px] text-muted-ink mt-2 leading-[1.4]">
                    &ldquo;{t.tagline}&rdquo;
                  </div>
                )}
                {t.changed_fields?.length > 0 && (
                  <div className="mt-3.5 flex flex-wrap gap-1.5">
                    {t.changed_fields.map((f) => (
                      <span
                        key={f}
                        className="text-[11px] eyebrow eyebrow-muted border border-bnm rounded-full px-2.5 py-1"
                      >
                        {label(f)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )
          )}

          {sending && (
            <div className="flex items-center gap-3 text-muted-ink text-[14px] mb-6">
              <span className="cine-bars" style={{ display: "inline-flex" }}>
                <span className="cine-bar" style={{ height: 14 }} />
                <span className="cine-bar" style={{ height: 14 }} />
                <span className="cine-bar" style={{ height: 14 }} />
              </span>
              Your Producer is reworking the plan…
            </div>
          )}

          {error && (
            <p className="text-denim-bnm text-sm mb-4">{error}</p>
          )}

          {!hasHistory && !sending && (
            <div className="flex flex-wrap gap-2 mb-4">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setInstruction(s)}
                  className="pill text-[13px] py-2 px-4"
                  data-testid={`revise-suggestion-${s.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Composer, pinned at the bottom */}
      <div className="border-t border-bnm px-6 md:px-10 py-5 shrink-0 bg-white">
        <div className="max-w-[720px] mx-auto">
          <div className="idea-shell px-5 md:px-7 py-4 md:py-5">
            <div className="flex items-end gap-3">
              <textarea
                autoFocus
                rows={1}
                value={instruction}
                onChange={(e) => {
                  setInstruction(e.target.value);
                  e.target.style.height = "auto";
                  e.target.style.height = e.target.scrollHeight + "px";
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    submit();
                  }
                }}
                placeholder={hasHistory ? "Ask for one more change…" : "e.g. Keep it India-only. Tighter budget."}
                className="idea-input idea-input--quiet flex-1 py-1 max-h-[140px]"
                data-testid="revise-input"
              />
              <button
                type="button"
                onClick={submit}
                disabled={!instruction.trim() || sending}
                aria-label="Send"
                className="send-btn shrink-0"
                data-testid="revise-submit"
              >
                <ArrowUp className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div className="mt-3 text-center">
            {hasHistory ? (
              <button
                type="button"
                onClick={onClose}
                className="text-[13px] text-muted-ink hover:text-ink transition-colors"
                data-testid="revise-done"
              >
                Done, back to the blueprint
              </button>
            ) : (
              <span className="eyebrow eyebrow-muted">Press Enter to send · Shift+Enter for new line</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
